import {useCallback, useMemo} from 'react'
import dayjs from 'dayjs'
import {AgendaEventAbsence, AgendaEventType, AgendaEventUnavailability, AgendaView} from '@/features/agenda/types'
import {formatLocaleDate} from '@/dayjs'
import {EventInteractionArgs} from 'react-big-calendar/lib/addons/dragAndDrop'
import {Formats} from 'react-big-calendar'

interface UseCalendarConfigurationProps {
    date: string
    view: AgendaView
    storedBlockouts: AgendaEventAbsence[]
    storedUnavailabilities: AgendaEventUnavailability[]
    isDisabledDay: (date: Date | string) => boolean
}

/**
 * Custom hook to handle calendar configuration
 * @param date - The date to display
 * @param view - The view to display
 * @param storedBlockouts - The blockouts to display
 * @param isDisabledDay - The function to check if a day is disabled
 * @returns The parsed date, min time, max time, formats, day prop getter, validate event move, validate event resize, validate slot selection, time slots, step minutes, resizable
 */
export const useCalendarConfiguration = ({
    date,
    view,
    storedBlockouts,
    storedUnavailabilities,
    isDisabledDay
}: UseCalendarConfigurationProps) => {
    // Time range configuration
    const parsedDate = useMemo(() => dayjs(date, 'DD-MM-YYYY').toDate(), [date])
    const minTime = useMemo(() => dayjs(parsedDate).set('hour', 8).toDate(), [parsedDate])
    const maxTime = useMemo(() => dayjs(parsedDate).set('hour', 20).toDate(), [parsedDate])

    // Calendar time format configuration
    const formats = useMemo(
        (): Formats => ({
            timeGutterFormat: (date: Date) => {
                return formatLocaleDate(date, 'HH:mm')
            },
            dayHeaderFormat: (date: Date) => {
                return formatLocaleDate(date, 'ddd')
            },
            selectRangeFormat: ({start, end}: {start: Date; end: Date}) => {
                return `${formatLocaleDate(start, 'HH:mm')} - ${formatLocaleDate(end, 'HH:mm')}`
            }
        }),
        []
    )

    // Calendar day styling
    const dayPropGetter = useCallback(
        (date: Date) => {
            // Check if the day is disabled (Sunday or holiday)
            if (isDisabledDay(date)) {
                return {
                    className: 'disabled-day'
                }
            }

            // Check if there's any blockout event that covers this day
            const currentDate = dayjs(date)
            const blockout = storedBlockouts.find(blockout => {
                const blockoutStart = dayjs(blockout.start).startOf('day')
                const blockoutEnd = dayjs(blockout.end).startOf('day')

                // Check if the current date is within the blockout period
                return (
                    currentDate.isSame(blockoutStart, 'day') ||
                    currentDate.isSame(blockoutEnd, 'day') ||
                    (currentDate.isAfter(blockoutStart, 'day') && currentDate.isBefore(blockoutEnd, 'day'))
                )
            })

            if (blockout) {
                // Apply a data attribute to be used by CSS for specific styling
                return {
                    className: `blockout-day absences`
                }
            }

            const unavailability = storedUnavailabilities.find(unavailability => {
                const unavailabilityStart = dayjs(unavailability.start).startOf('day')
                const unavailabilityEnd = dayjs(unavailability.end).startOf('day')
                // Check if the current date is within the unavailability period
                return (
                    currentDate.isSame(unavailabilityStart, 'day') ||
                    currentDate.isSame(unavailabilityEnd, 'day') ||
                    (currentDate.isAfter(unavailabilityStart, 'day') && currentDate.isBefore(unavailabilityEnd, 'day'))
                )
            })

            if (unavailability) {
                return {
                    className: `blockout-day`
                }
            }

            return {}
        },
        [isDisabledDay, storedBlockouts]
    )

    const eventPropGetter = useCallback((event: AgendaEventType) => {
        const className = event.status == 'tentative' ? 'tentative' : ''
        return {
            className
        }
    }, [])

    // Event validation helpers
    const validateEventMove = useCallback(
        (event: EventInteractionArgs<AgendaEventType>) => {
            // Prevent events from being moved to disabled days
            return !(isDisabledDay(event.start) || isDisabledDay(event.end) || event.event.status == 'tentative')
        },
        [isDisabledDay]
    )

    const validateEventResize = useCallback(
        (event: EventInteractionArgs<AgendaEventType>) => {
            // Check if the event is on a disabled day
            if (isDisabledDay(event.start) || isDisabledDay(event.end) || event.event.status == 'tentative') {
                return false
            }

            // Check if the duration is at least 15 minutes
            const duration = dayjs(event.end).diff(dayjs(event.start), 'minute')
            return duration >= 15
        },
        [isDisabledDay]
    )

    // Handle slot selection
    const validateSlotSelection = useCallback(
        (slotInfo: {start: Date; end: Date}) => {
            return !isDisabledDay(slotInfo.start) || view == 'month'
        },
        [isDisabledDay, view]
    )

    return {
        parsedDate,
        minTime,
        maxTime,
        formats,
        dayPropGetter,
        eventPropGetter,
        validateEventMove,
        validateEventResize,
        validateSlotSelection,
        timeSlots: 1,
        stepMinutes: 60,
        resizable: view != 'month'
    }
}
